import { computed, ref, Ref } from '@vue/composition-api';

import {
  useAllowlist,
  usePixelInfoApi,
  useExternalUrlDomainApi,
  useCreativeActionTypeIs,
  useRequest
} from '@tt4b/ttam-hooks';
import {
  AdgroupFormData,
  CATALOG_CREATIVE_TYPE,
  FullNormalFormData,
  genDestinationUrlTypes,
  DestinationUrlType
} from '@tt4b/ttam-enums';
import { i18n } from '@tt4b/ttam-i18n';

const PixelOnlyTracking = 'null';

/** pixel info hook */
export function usePixelInfo(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const { data: pixelInfo, run: loadPixelInfo } = useRequest(usePixelInfoApi, {
    pixelId: adgroupData.value?.adRefPixelId || '',
    promotionWebsiteType: adgroupData.value?.promotionWebsiteType || 0,
  });
  const pixelEvents = computed(() => pixelInfo.value?.pixelEvents ?? []);

  return {
    pixelInfo,
    pixelEvents,
    loadPixelInfo,
  };
}

/** pixel domain hook */
export function usePixelDomainProps(options: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  createMode: Ref<string>;
}) {
  const { adgroupData, creativeData: formData, createMode } = options;

  const { isPcmPhase2White } = useAllowlist();
  const { isEdit, isCopy } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const { pixelInfo: curPixelInfo, pixelEvents, loadPixelInfo } = usePixelInfo({ adgroupData });

  const hasPixel = computed(() => adgroupData.value.adRefPixelId);
  const isPixelTracking = computed(() => formData.value.domain === PixelOnlyTracking);

  const pixelLabel = computed(() => (adgroupData.value?.adRefPixelId ? curPixelInfo.value?.name : '-'));
  const externalUrl = computed(() =>
    (formData.value.externalUrls?.length > 0 ? formData.value.externalUrls[0] ?? '' : formData.value.externalUrl)
  );

  const eventDomainList = computed(() => {
    const pixelEvent = pixelEvents.value.filter(item => item.id === adgroupData.value.externalAction);
    return pixelEvent?.[0]?.verifiedDomainList ?? [];
  });

  const isPcmPixel = computed(
    () =>
      isPcmPhase2White.value &&
      adgroupData.value.adRefPixelId &&
      !adgroupData.value.promotionWebsiteType &&
      eventDomainList.value.length
  );

  const isNotSupportDomain = computed(() => {
    const isCollectionAds = formData.value.creativeType === CATALOG_CREATIVE_TYPE.LYNX_PLP;
    const isInstantPage =
      formData.value.destinationUrlType === genDestinationUrlTypes()[DestinationUrlType.instantPage].name;
    const isCardChecked = Boolean(formData.value.cardId);

    return isInstantPage || isCollectionAds || isCardChecked || formData.value?.pageId;
  });

  /** ca instant page 不支持domain */
  const isSupportDomain = computed(() => isPcmPixel.value && !isNotSupportDomain.value);

  const domainComputed = computed(() => {
    if ((isEdit.value || isCopy.value) && isSupportDomain.value && !formData.value?.domain) {
      return PixelOnlyTracking;
    }
    // domain存在且不再当前列表里面, 或者不支持domain了清空
    if (
      !isSupportDomain.value ||
      (formData.value.domain && !isPixelTracking.value && !eventDomainList.value.includes(formData.value.domain))
    ) {
      return '';
    }

    return formData.value.domain;
  });

  const isDomainNotMatch = computed(
    () =>
      isPcmPhase2White.value &&
      externalUrl.value &&
      formData.value.externalUrlDomain &&
      domainComputed.value &&
      domainComputed.value !== formData.value.externalUrlDomain &&
      !isPixelTracking.value
  );

  // 没有domain或者选了pixelonly tracking的，但是落地页domain在认证的domain集合里面，真实投放是不drop流量了，不显示drop msg
  const isShowPcmDrop = computed(
    () =>
      isPcmPhase2White.value &&
      hasPixel.value &&
      !isNotSupportDomain.value &&
      (isPixelTracking.value || !eventDomainList.value.length || isDomainNotMatch.value)
  );

  async function initExternalUrlDomain(url = '', shouldSetDomain = false) {
    if (!isPcmPixel.value || !url) {
      return {};
    }
    const externalUrlDomain = await useExternalUrlDomainApi({ external_url: url });
    const isUrlDomainInEventDomainList = eventDomainList.value?.includes(externalUrlDomain);

    return {
      externalUrlDomain,
      domain: isUrlDomainInEventDomainList && shouldSetDomain ? externalUrlDomain : undefined,
    };
  }

  const trackingOptions = computed(() => [
    {
      label: i18n.t('module_domain_tracking'),
      options: eventDomainList.value.map(item => ({
        value: item,
        label: item,
        subInfo: `Pixel ID: ${adgroupData.value?.adRefPixelId}`,
        type: 'domain',
      })),
    },
    {
      label: i18n.t('module_pixel_tracking'),
      type: 2,
      options: [
        {
          label: pixelLabel.value,
          subInfo: `Pixel ID: ${adgroupData.value?.adRefPixelId}`,
          value: PixelOnlyTracking,
          type: 'pixel',
        },
      ],
    },
  ]);

  return {
    domainComputed,
    isPcmPixel,
    isShowPcmDrop,
    isSupportDomain,
    isDomainNotMatch,
    isNotSupportDomain,

    pixelLabel,
    externalUrl,
    eventDomainList,
    trackingOptions,
    loadPixelInfo,
    initExternalUrlDomain,
  };
}
